import { defineStore } from 'pinia';
import { Api } from '@/Api';
import { useViewerItemStore } from '@/modules/viewer/store/useViewerItemStore';
import { useOrganizationStore } from '@/store/useOrganizationStore';
import { useFeatures } from '@/composables/useFeatures';
export interface Integration {
  name: string;
  description: string;
  enabled: boolean;
  icon: string;
  color: string;
  requiresRequest?: boolean;
}

export interface StaticIntegration {
  name: string;
  description: string;
  url: string;
  requiresBusinessPlan: boolean;
  disabled?: boolean;
}

export interface IntegrationState {
  integrations: Integration[];
  commingSoonIntegrationsData: { name: string; description: string }[];
  staticIntegrationsData: StaticIntegration[];
  projectId: string | null;
  token: string | null;
  loading: boolean;
  error: string | null;
}

export const useIntegrationStore = defineStore('integrationStore', {
  state: () =>
    ({
      integrations: [],
      commingSoonIntegrationsData: [
        {
          name: 'Google Workspace',
          description: 'Send step-by-step guides to Google Docs and Google Slides, and save videos directly to Google Drive',
        },
        {
          name: 'Salesforce Service Cloud',
          description: "Enhance your Salesforce workflows with Zight's screen recording capabilities",
        },
        {
          name: 'ServiceNow',
          description: 'Streamline IT service management with integrated screen recording and sharing',
        },
        {
          name: 'Freshdesk',
          description: 'Provide faster support resolution with visual troubleshooting guides and screen recordings',
        },
        {
          name: 'Linear',
          description: 'Attach videos, screenshots and more to your Linear tickets, create clear documentation from videos automatically',
        },
      ],
      staticIntegrationsData: [
        {
          name: 'Intercom',
          description: 'Send and receive videos directly from Intercom to provide better customer support',
          url: 'https://app.intercom.com/oauth?client_id=324bdbe1-dcbf-415b-a4a2-f8f1ff4300c4',
          requiresBusinessPlan: true,
        },
        {
          name: 'Zendesk',
          description: 'Create and share videos directly from Zendesk to provide better customer support',
          url: 'https://www.zendesk.com/marketplace/apps/support/110120/zight/',
          requiresBusinessPlan: true,
        },
        {
          name: 'Jira',
          description: 'Create videos directly from your Jira tickets',
          url: 'https://marketplace.atlassian.com/apps/1228444/cloudapp-screen-recorder-for-jira?tab=overview&hosting=cloud',
          requiresBusinessPlan: true,
        },
        {
          name: 'Slack',
          description: 'Create videos directly from Slack, no app install needed on your computer, and receive notifications from engagement on your content.',
          url: 'https://cloudapp.slack.com/marketplace/A037KHLBMKK-zight-screen-recorder',
          requiresBusinessPlan: false,
        },
        {
          name: 'Teams',
          description: 'Get notified in Microsoft Teams when someone views your Zight videos, gifs and screenshots.',
          url: 'https://appsource.microsoft.com/en-us/product/office/wa200005310?tab=overview',
          requiresBusinessPlan: false,
        },
        {
          name: 'Lucid',
          description:
            "Share information faster and with more context and clarity on Lucid's collaborative platform with the power of Zight's screen and video recordings natively where work is happening.",
          url: 'https://lucid.co/marketplace/01d901ca/zight',
          requiresBusinessPlan: false,
        },
        {
          name: 'Zight SDK',
          description: 'Add video recording capabilities to your products',
          url: 'https://zight.com/sdk/',
          requiresBusinessPlan: false,
        },
      ],
      projectId: null,
      token: null,
      loading: false,
      error: null,
    }) as IntegrationState,
  actions: {
    async fetchIntegrations(forceRefresh: boolean = false) {
      this.$patch({
        loading: true,
        error: null,
      });
      try {
        const response = await Api.integrations.list(forceRefresh);
        const integrations = response.data?.integrations?.attributes || [];
        const projectId = response.data?.projectId?.attributes;
        const token = response.data?.token?.attributes;
        this.$patch({
          integrations: integrations,
          projectId: projectId,
          token: token,
        });
      } catch (err: any) {
        this.$patch({
          error: err.message || 'Failed to fetch integrations',
        });
      } finally {
        this.$patch({
          loading: false,
        });
      }
    },
    async sendTo(integration: string) {
      const itemStore = useViewerItemStore();
      if (!itemStore.id) {
        return;
      }
      await Api.integrations.send(integration, itemStore.id);
    },
    setIntegrationToken(token: string) {
      this.token = token;
    },
  },
  getters: {
    commingSoonIntegrations: state => state.commingSoonIntegrationsData,
    sortedComingSoonIntegrations: state => {
      return [...state.commingSoonIntegrationsData].sort((a, b) => a.name.localeCompare(b.name));
    },
    staticIntegrations: state => state.staticIntegrationsData,
    sortedStaticIntegrations: (state): StaticIntegration[] => {
      const canAccessWorkflowIntegrations = useOrganizationStore().canAccessWorkflowIntegrations;

      return [...state.staticIntegrationsData]
        .map(integration => ({
          ...integration,
          disabled: integration.requiresBusinessPlan ? !canAccessWorkflowIntegrations : false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
    sortedIntegrations: (state): Integration[] => {
      const organizationStore = useOrganizationStore();
      const isFreeUser = organizationStore.free;
      const { getFeature } = useFeatures();
      const workflowIntegrationsFlag = getFeature<boolean>('workflow-integrations', false);

      return [...state.integrations]
        .map(integration => ({
          ...integration,
          requiresRequest: !integration.enabled ? isFreeUser || !workflowIntegrationsFlag.value : false,
        }))
        .sort((a, b) => a.name.localeCompare(b.name));
    },
  },
});
